import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  Signal,
  signal,
  viewChildren,
  WritableSignal,
  effect,
  untracked,
} from '@angular/core';
import { SidePanelService } from '../services/side-panel.service';
import { PanelLayer } from '../types/panel-layer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, fromEvent, map, takeUntil, tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';

@Component({
  selector: 'app-side-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, LucideIconsModule],
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
})
export class SidePanelComponent implements AfterViewInit {
  @Input() appContent: HTMLDivElement;
  layers: WritableSignal<PanelLayer[]> = signal([]);

  protected readonly currentPanelIndex: WritableSignal<number> = signal(0);
  protected readonly zIndexMultiplier = 5;

  protected readonly canNavigateBack = computed(() => {
    return this.currentPanelIndex() > 0;
  });
  protected readonly canNavigateForward = computed(() => {
    return this.currentPanelIndex() < this.layers().length - 1;
  });
  protected readonly showNavigationControls = computed(() => {
    return this.layers().length > 1;
  });

  @HostListener('window:keydown.escape', ['$event'])
  handleKeyDown(event: Event) {
    if (
      this.isOpen &&
      event.target instanceof HTMLElement &&
      (event.target.nodeName === 'BODY' || this.isInside(event.target))
    ) {
      this.closeCurrentPanel(event as KeyboardEvent);
    }
  }

  protected panelContents: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren('panelContent');

  protected panelLayers: WritableSignal<(PanelLayer & { animationDisabled: boolean })[]> = signal([]);

  protected readonly isResizing = signal(false);

  private readonly panelOwner: WritableSignal<HTMLElement | null> = signal(null);

  // Track when the panel was last opened to prevent immediate close from click events
  // Initialize to current time to prevent any race conditions on initial page load
  private _lastOpenTime = Date.now();

  // Flag to block navigation during panel close operations
  // Using a simple boolean for synchronous checking - signals have timing issues with change detection
  private _navigationBlockedUntil = 0;

  constructor(
    private readonly _sidePanelService: SidePanelService,
    private readonly _renderer: Renderer2,
    private readonly _host: ElementRef,
    private readonly _destroyRef: DestroyRef
  ) {
    effect(
      () => {
        const currentLayers = this.layers();
        this._updatePanelLayers(currentLayers);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const layersCount = this.layers().length;
        const currentIndex = untracked(() => this.currentPanelIndex());
        if (layersCount > 0 && currentIndex >= layersCount) {
          this.currentPanelIndex.set(layersCount - 1);
        }
      },
      { allowSignalWrites: true }
    );

    this._setupWindowResizeListener();
  }

  ngAfterViewInit(): void {
    this._sidePanelService.panel = this;
    this._closePanelsIfClickedOutside();
  }

  public get isOpen() {
    return this.layers().length;
  }

  public openStacked(owner: HTMLElement, layers: PanelLayer[], preserveIndex = false) {
    this.panelOwner.set(owner);
    this.layers.set(layers);

    if (preserveIndex) {
      const currentIdx = this.currentPanelIndex();
      if (currentIdx >= layers.length) {
        this.currentPanelIndex.set(Math.max(0, layers.length - 1));
      }
    } else {
      this.currentPanelIndex.set(layers.length - 1);
    }

    this._lastOpenTime = Date.now();
  }

  public closeAllStackedPanels(): void {
    this.panelOwner.set(null);
    this.layers.set([]);
    this.currentPanelIndex.set(0);
  }

  protected navigateBack($event: MouseEvent): void {
    $event.stopPropagation();

    if (this._isNavigationBlocked() || !this.canNavigateBack()) {
      return;
    }

    this.currentPanelIndex.set(this.currentPanelIndex() - 1);
  }

  protected navigateForward($event: MouseEvent): void {
    $event.stopPropagation();

    if (this._isNavigationBlocked() || !this.canNavigateForward()) {
      return;
    }

    this.currentPanelIndex.set(this.currentPanelIndex() + 1);
  }

  protected closeAllPanels($event: MouseEvent): void {
    $event.stopPropagation();
    this._sidePanelService.close();
  }

  public isInside(target: HTMLElement) {
    return (this._host.nativeElement as HTMLElement).contains(target);
  }

  /**
   * Returns a unique identifier for tracking a panel layer in the @for loop.
   * Uses the actual content ID (resource, violation, etc.) instead of stackKey
   * to ensure Angular correctly identifies which DOM elements to remove/keep
   * when panels are closed and stackKeys get reassigned.
   */
  protected getLayerTrackId(layer: PanelLayer & { animationDisabled: boolean }): string {
    return (
      (layer.inputBindings['resource'] as string) ??
      (layer.inputBindings['violation'] as string) ??
      (layer.inputBindings['issue'] as string) ??
      (layer.inputBindings['finding'] as string) ??
      (layer.inputBindings['dataItem'] as string) ??
      (layer.inputBindings['inLineControl'] as string) ??
      (layer.inputBindings['agentStep'] as string) ??
      layer.stackKey
    );
  }

  protected closeCurrentPanel($event?: MouseEvent | KeyboardEvent) {
    $event?.stopPropagation();

    // Ignore clicks that arrive shortly after a panel was opened/reopened.
    // When a stacked panel is closed, the revealed panel briefly keeps its
    // old (lower) z-index while the overlay still has a higher one.
    // Phantom or rapid follow-up clicks can hit the overlay during this
    // window and unintentionally close the revealed panel as well.
    const timeSinceOpen = Date.now() - this._lastOpenTime;
    if (timeSinceOpen < 150) {
      return;
    }

    // Block navigation for 200ms to prevent phantom click events
    // that can occur during DOM updates from triggering unwanted navigation
    this._navigationBlockedUntil = Date.now() + 200;

    const currentLayer = this.layers()[this.currentPanelIndex()];
    if (currentLayer) {
      this._sidePanelService.closePanel([currentLayer.stackKey]);
    }
  }

  private _isNavigationBlocked(): boolean {
    return Date.now() < this._navigationBlockedUntil;
  }

  /**
   * Calculates the z-index for a panel based on whether it's the current one.
   * The current panel gets the highest z-index to appear on top.
   */
  protected getPanelZIndex(index: number): number {
    const currentIdx = this.currentPanelIndex();
    const total = this.panelLayers().length;

    if (index === currentIdx) {
      return (total + 1) * this.zIndexMultiplier;
    }
    return (index + 1) * this.zIndexMultiplier;
  }

  /**
   * Calculates the left offset for a panel.
   * All panels use the same offset since non-current panels are hidden.
   */
  protected getPanelOffset(): string {
    return 'var(--sidepanel-offset)';
  }

  protected onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = this._sidePanelService.panelWidth();

    this.isResizing.set(true);

    const mousemove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const mouseup$ = fromEvent<MouseEvent>(document, 'mouseup');

    mousemove$
      .pipe(
        map((moveEvent) => {
          const deltaX = startX - moveEvent.clientX;
          return startWidth + deltaX;
        }),
        takeUntil(mouseup$.pipe(tap(() => this.isResizing.set(false)))),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((newWidth) => {
        this._sidePanelService.setWidth(newWidth);
      });
  }

  private _setupWindowResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._sidePanelService.setWidth(this._sidePanelService.panelWidth());
      });
  }

  private _updatePanelLayers(currentLayers: PanelLayer[]) {
    const previousPanels = untracked(() => this.panelLayers());
    const previousPanelContents = untracked(() => this.panelContents());

    const newLayers = currentLayers.map((layer, index) => {
      const panelHadSameContentComponent = previousPanels[index]?.component.name === layer.component.name;
      if (panelHadSameContentComponent && previousPanelContents[index]?.nativeElement) {
        // If animation on this layer previously was disabled, we want to enable it to animate closing
        // It can be done only manually with renderer, as Angular cannot update "@.disabled" for previous layer
        // and at the same time create new layers
        // Check comment for more info https://github.com/angular/components/issues/4227#issuecomment-348148642
        this._renderer.setProperty(previousPanelContents[index].nativeElement, '@.disabled', true);
      }
      return { ...layer, animationDisabled: panelHadSameContentComponent };
    });

    const allPanelsAreClosed = previousPanels.length > 0 && currentLayers.length === 0;
    if (allPanelsAreClosed) {
      previousPanelContents.forEach((content) => {
        if (content?.nativeElement) {
          this._renderer.setProperty(content.nativeElement, '@.disabled', false);
        }
      });
    }

    this.panelLayers.set(newLayers);
  }

  private _closePanelsIfClickedOutside() {
    // The side-panel registers click listeners on the app-content div to detect outside clicks.
    // We don't want to register the click handlers on the body/document to prevent interference with dialogs
    fromEvent<PointerEvent>(this.appContent, 'click')
      .pipe(
        filter(() => !!this.panelOwner()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event: PointerEvent) => {
        const clickTarget = event.target as HTMLElement;

        // Ignore clicks that happen within 150ms of opening the panel
        // This prevents the panel from closing immediately when clicking a table row to open it
        const timeSinceOpen = Date.now() - this._lastOpenTime;
        if (timeSinceOpen < 150) {
          return;
        }

        // Ignore clicks inside tables and graphs - row/node selection should open/navigate panels, not close them
        if (
          clickTarget.closest('table') ||
          clickTarget.closest('p-table') ||
          clickTarget.closest('app-prime-table') ||
          clickTarget.closest('app-relations-graph')
        ) {
          return;
        }

        // If the click was inside the side-panel, or the host, do nothing, otherwise, close
        const ownerClicked = this.panelOwner()?.contains(event.target as Node);
        const hostClicked = this._host?.nativeElement?.contains(event.target);
        if (ownerClicked || hostClicked) {
          return;
        }

        this._sidePanelService.close();
      });
  }
}
